;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import * as Sentry from "@sentry/nextjs";
import {
	METRIC_DEFINITIONS,
	type MetricDefinition,
} from "@/lib/observability/metric-definitions";

export type MetricName = keyof typeof METRIC_DEFINITIONS;

type MetricAttributeKey<Name extends MetricName> =
	(typeof METRIC_DEFINITIONS)[Name]["attributes"][number];

export type MetricAttributes<Name extends MetricName> = Record<
	MetricAttributeKey<Name>,
	string
>;

export type MetricOutcome = "ok" | "rejected" | "unauthorized" | "error";

/** Maps an HTTP status (or a gRPC error's HTTP equivalent) to an outcome. */
export function outcomeFromStatus(status: number): MetricOutcome {
	if (status >= 500) {
		return "error";
	}
	if (status === 401 || status === 403) {
		return "unauthorized";
	}
	if (status >= 400) {
		return "rejected";
	}
	return "ok";
}

type TimedMetricName = {
	[Name in MetricName]: (typeof METRIC_DEFINITIONS)[Name]["type"] extends "distribution"
		? "outcome" extends MetricAttributeKey<Name>
			? Name
			: never
		: never;
}[MetricName];

export function recordMetric<Name extends MetricName>({
	attributes,
	name,
	value = 1,
}: {
	attributes: MetricAttributes<Name>;
	name: Name;
	value?: number;
}): void {
	const definition: MetricDefinition = METRIC_DEFINITIONS[name];
	const options = { attributes, unit: definition.unit };

	// Telemetry must never break the code path it measures.
	try {
		if (definition.type === "count") {
			Sentry.metrics?.count?.(name, value, options);
			return;
		}
		if (definition.type === "gauge") {
			Sentry.metrics?.gauge?.(name, value, options);
			return;
		}
		Sentry.metrics?.distribution?.(name, value, options);
	} catch {
		// Ignore: the SDK is missing or not initialised.
	}
}

export async function withMetricTiming<Name extends TimedMetricName, Result>({
	attributes,
	callback,
	describeError,
	name,
	resolveErrorOutcome,
	resolveOutcome,
}: {
	attributes: Omit<MetricAttributes<Name>, "outcome">;
	callback: () => Promise<Result>;
	describeError?: (
		error: unknown,
	) => Partial<Omit<MetricAttributes<Name>, "outcome">>;
	name: Name;
	/** Outcome of a thrown error; defaults to `error`. */
	resolveErrorOutcome?: (error: unknown) => MetricOutcome;
	resolveOutcome?: (result: Result) => MetricOutcome;
}): Promise<Result> {
	const startedAt = performance.now();
	const record = (
		outcome: MetricOutcome,
		extraAttributes: Partial<Omit<MetricAttributes<Name>, "outcome">> = {},
	) => {
		recordMetric({
			attributes: {
				...attributes,
				...extraAttributes,
				outcome,
			} as MetricAttributes<Name>,
			name,
			value: performance.now() - startedAt,
		});
	};

	let result: Result;
	try {
		result = await callback();
	} catch (error) {
		let errorAttributes: Partial<Omit<MetricAttributes<Name>, "outcome">> = {};
		let errorOutcome: MetricOutcome = "error";
		try {
			errorAttributes = describeError?.(error) ?? {};
			errorOutcome = resolveErrorOutcome?.(error) ?? "error";
		} catch {
			// Keep the original error.
		}
		record(errorOutcome, errorAttributes);
		throw error;
	}

	let outcome: MetricOutcome = "ok";
	try {
		outcome = resolveOutcome?.(result) ?? "ok";
	} catch {
		// Telemetry must not change the result.
	}
	record(outcome);
	return result;
}
